/** Sign-in page: workspace slug + SSO discovery + email/password + 2FA challenge. */
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthError, fetchSessionState, redirectToSsoProvider } from "@/api/auth";
import {
  api,
  getSubdomainTenantSlug,
  getTenantSlug,
  setTenantSlug,
  type Schemas,
} from "@/api/client";
import { ApiError, unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { LanguageSwitcher } from "@/app/shell/AppShell";
import { SmartWatersMark, ThemeToggle } from "@/shared/ui/brand";
import { Button } from "@/shared/ui/button";
import { Field, Input } from "@/shared/ui/form";

export type PublicSsoProvider = Schemas["PublicSsoProviderOut"];

const SSO_ERROR_KEYS: Record<string, string> = {
  sso_domain_not_allowed: "auth.ssoDomainNotAllowed",
  sso_no_membership: "auth.ssoNoMembership",
  membership_disabled: "auth.membershipDisabled",
  sso_unavailable: "auth.ssoUnavailable",
  sso_missing_email: "auth.ssoMissingEmail",
};

/** Translate a login failure into a message key/argument pair. */
export function describeLoginError(
  error: unknown,
  slug: string,
): { key: string; values?: Record<string, string> } {
  if (error instanceof AuthError) {
    if (error.code === "sso_required") {
      return { key: "auth.ssoRequired" };
    }
    if (error.code === "incorrect_code") {
      return { key: "auth.invalidMfaCode" };
    }
    if (error.pendingFlows.includes("verify_email")) {
      return { key: "auth.verifyEmail" };
    }
    return error.pendingFlows.length ? { key: "auth.mfaRequired" } : { key: "auth.invalid" };
  }
  if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
    return { key: "auth.noAccess", values: { slug } };
  }
  return { key: "app.error" };
}

/** Login form with SSO buttons, password sign-in, and two-factor authentication step. */
export function LoginPage(): ReactElement {
  const { t } = useTranslation();
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const subdomainSlug = getSubdomainTenantSlug();
  const [tenant, setTenant] = useState(() => subdomainSlug || getTenantSlug());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState<string | null>(() => {
    const params = new URLSearchParams(globalThis.location?.search ?? "");
    const errCode = params.get("error");
    return errCode ? (SSO_ERROR_KEYS[errCode] ?? "app.error") : null;
  });
  const [busy, setBusy] = useState(false);

  const cleanTenant = tenant.trim().toLowerCase();
  const authOptionsQuery = useQuery({
    queryKey: ["auth-options", cleanTenant],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/identity/auth-options", {
          params: { query: { tenant: cleanTenant } },
        }),
      ),
    enabled: cleanTenant.length >= 2,
    staleTime: 30_000,
  });
  const ssoProviders: PublicSsoProvider[] = authOptionsQuery.data?.sso_providers ?? [];

  useQuery({
    queryKey: ["session-pending-mfa"],
    queryFn: async () => {
      const state = await fetchSessionState();
      if (state.pendingFlows.includes("mfa_authenticate")) {
        setMfaPending(true);
      }
      return state;
    },
    staleTime: 10_000,
  });

  const submit = async (e: { preventDefault(): void }): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mfaPending) {
        await verifyMfa(cleanTenant, mfaCode);
      } else {
        await login(cleanTenant, email, password);
      }
      await navigate({ to: "/" });
    } catch (err) {
      if (
        !mfaPending &&
        err instanceof AuthError &&
        err.pendingFlows.includes("mfa_authenticate")
      ) {
        setMfaPending(true);
        setMfaCode("");
        return;
      }
      const d = describeLoginError(err, cleanTenant);
      setError(t(d.key, d.values));
    } finally {
      setBusy(false);
    }
  };

  const startSso = (provider: PublicSsoProvider): void => {
    if (cleanTenant) {
      setTenantSlug(cleanTenant);
    }
    const origin = globalThis.location?.origin ?? "http://localhost:5173";
    const callbackUrl = cleanTenant
      ? `${origin}/?tenant=${encodeURIComponent(cleanTenant)}`
      : `${origin}/`;
    redirectToSsoProvider(provider.provider_id, callbackUrl);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-lg border border-t-3 border-t-brand bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-sidebar px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <SmartWatersMark className="size-7" />
            <div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-bold tracking-tight text-foreground">{t("app.brand")}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-brand-foreground">
                  Smart Waters
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{t("app.tagline")}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>
        <div className="space-y-4 p-5">
          <form onSubmit={(e) => void submit(e)} className="space-y-3.5" noValidate>
            {!mfaPending ? (
              <>
                <Field id="tenant" label={t("auth.workspace")} hint={t("auth.workspaceHelp")}>
                  <Input
                    id="tenant"
                    value={tenant}
                    onChange={(e) => setTenant(e.target.value)}
                    autoComplete="organization"
                    readOnly={Boolean(subdomainSlug)}
                    required
                  />
                </Field>
                {ssoProviders.length > 0 ? (
                  <div className="space-y-2 pt-1">
                    {ssoProviders.map((provider) => (
                      <Button
                        key={provider.provider_id}
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => startSso(provider)}
                      >
                        {t("auth.continueWithSso", { provider: provider.name })}
                      </Button>
                    ))}
                    <div className="relative py-1 text-center text-[11px] text-muted-foreground">
                      <span>{t("auth.orContinueWithEmail")}</span>
                    </div>
                  </div>
                ) : null}
                <Field id="email" label={t("auth.email")}>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </Field>
                <Field id="password" label={t("auth.password")}>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </Field>
                <div className="flex justify-end">
                  <Link
                    to="/auth/reset-password"
                    className="text-xs font-medium text-brand-foreground underline underline-offset-4 hover:text-primary"
                  >
                    {t("auth.forgotPassword")}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">{t("auth.mfaPrompt")}</p>
                <Field id="mfa-code" label={t("auth.mfaCode")}>
                  <Input
                    id="mfa-code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    required
                  />
                </Field>
              </>
            )}
            {error ? (
              <p role="alert" className="text-xs text-destructive">
                {error.startsWith("auth.") || error === "app.error" ? t(error) : error}
              </p>
            ) : null}
            <Button
              type="submit"
              className="w-full"
              disabled={
                busy ||
                (!mfaPending && (!cleanTenant || !email || !password)) ||
                (mfaPending && !mfaCode.trim())
              }
            >
              {busy ? t("auth.signingIn") : mfaPending ? t("auth.verifyCode") : t("auth.signIn")}
            </Button>
            {mfaPending ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setMfaPending(false);
                  setMfaCode("");
                  setError(null);
                }}
              >
                {t("auth.backToSignIn")}
              </Button>
            ) : null}
          </form>
          <div className="border-t pt-3 text-center">
            <Link
              to="/docs"
              className="text-xs font-medium text-brand-foreground underline underline-offset-4 hover:text-primary"
            >
              {t("docs.title")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
